import React, { useState, useEffect, useRef } from "react";
import { isEmpty } from "lodash";

import { Spinner } from 'react-bootstrap';
import "../../../../css/layouts.css"

import AspectRatioController from "./AspectRatioController";
import { COMMON_ASPECT_RATIOS, layoutColor } from '../../../../../../config/layoutConstants';

import { useSelector, useDispatch } from 'react-redux';
import { useFetchAllAspectRatio } from "../../../../../../queries/layouts";
import { setSelectedArea } from "../../../../../../redux/Slices/layoutSlice";
import { toggleLoader } from '../../../../../../redux/Slices/Global/globalSlice';
import AddArea from "./AddArea";
import VisualAreaContent from "./VisualAreaContent";
import GridOverlay from "./Tools/GridOverlay";
import RulersOverlay from "./Tools/RulersOverlay";
import AlignmentGuides from "./Tools/AlignmentGuides";
import SafeZones from "./Tools/SafeZones";
import CrosshairOverlay from "./Tools/CrosshairOverlay";

const DynamicContainer = ({ 
    areas, 
    setAreas, 
    currentAspectRatio, 
    setCurrentAspectRatio, 
    scrollableDivRef, 
    endPositionArea = null, 
    layoutColor = [], 
    zoomLevel = 1,
    toolsState = null,
    isCanvasFullscreen = false,
    isPaused = false,
    setAreaColor = null,
    setAreaOpacity = null
}) => {
    const dispatch = useDispatch();
    const drawingAreaRef = useRef(null)
    const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
    const { selectedTemplate, selectedLayout, layoutAspectRatio, unit: reduxUnit, selectedArea } = useSelector((state) => state.layout);
    const unit = reduxUnit || { value: "%" };

    // Default to Landscape 16:9 so the canvas renders immediately,
    // even before the aspect-ratio fetch resolves.
    const [aspectWidth, setAspectWidth] = useState(COMMON_ASPECT_RATIOS["16:9"].width);
    const [aspectHeight, setAspectHeight] = useState(COMMON_ASPECT_RATIOS["16:9"].height);
    const [aspectRatios, setAspectRatios] = useState([]);
    const { data: allAspectRatio, isLoading: isFetching } = useFetchAllAspectRatio();

    useEffect(() => {
        const updateDimensions = () => {
            if (drawingAreaRef.current) {
                const { offsetWidth, offsetHeight } = drawingAreaRef.current;
                setDimensions({ width: offsetWidth, height: offsetHeight });
            }
        };

        if (!isEmpty(allAspectRatio)) {
            dispatch(toggleLoader("createLoading"))
            setCurrentAspectRatio(layoutAspectRatio);
            updateDimensions();
            window.addEventListener("resize", updateDimensions);
            const data = allAspectRatio?.data?.data;
            setAspectRatios(data)
            if (selectedTemplate?.aspect_ratio_id) {
                const selectedAspect = data?.find(item => item.id === selectedTemplate.aspect_ratio_id);
                setCurrentAspectRatio(selectedAspect);
            } else if (selectedLayout?.aspect_ratio_id) {
                const selectedAspect = data?.find(item => item.id === selectedLayout.aspect_ratio_id);
                setCurrentAspectRatio(layoutAspectRatio);
            } else {
                // Default to Landscape (16:9); fall back to the first wide ratio, then the first row
                const landscape = data?.find(item => Number(item.width) === 16 && Number(item.height) === 9)
                    ?? data?.find(item => Number(item.width) > Number(item.height))
                    ?? data?.[0];
                setCurrentAspectRatio(landscape);
            }
            dispatch(toggleLoader("createLoading"))
            return () => {
                window.removeEventListener("resize", updateDimensions);
            };
        }
    }, [allAspectRatio]);

    useEffect(() => {
        // Guard width/height: an empty {} must not wipe the 16:9 defaults
        if (currentAspectRatio?.width && currentAspectRatio?.height) {
            const aspectKey = `${currentAspectRatio.width}:${currentAspectRatio.height}`;

            if (COMMON_ASPECT_RATIOS[aspectKey]) {
                setAspectWidth(COMMON_ASPECT_RATIOS[aspectKey].width);
                setAspectHeight(COMMON_ASPECT_RATIOS[aspectKey].height);
            } else {
                setAspectWidth(currentAspectRatio.width);
                setAspectHeight(currentAspectRatio.height);
            }
        }
    }, [currentAspectRatio]);

    // Update dimensions when fullscreen mode changes
    useEffect(() => {
        const updateDimensions = () => {
            if (drawingAreaRef.current) {
                const { offsetWidth, offsetHeight } = drawingAreaRef.current;
                setDimensions({ width: offsetWidth, height: offsetHeight });
            }
        };

        // Small delay to allow CSS transition to complete
        const timer = setTimeout(updateDimensions, 100);
        return () => clearTimeout(timer);
    }, [isCanvasFullscreen]);

    // Measure the drawing area as soon as it is in the DOM (isFetching flips
    // false) so the canvas renders immediately with the default 16:9 ratio
    // instead of waiting for the aspect-ratio effect to run.
    useEffect(() => {
        if (!isFetching && drawingAreaRef.current) {
            const { offsetWidth, offsetHeight } = drawingAreaRef.current;
            setDimensions({ width: offsetWidth, height: offsetHeight });
        }
    }, [isFetching]);


    const calculateScaledDimensions = () => {
        if (!aspectWidth || !aspectHeight) return { width: 0, height: 0 };
        let widthRatio, heightRatio;

        widthRatio = dimensions?.width / aspectWidth;
        heightRatio = dimensions?.height / aspectHeight;

        const scaleFactor = Math.min(widthRatio, heightRatio);

        const scaledWidth = aspectWidth * scaleFactor;
        const scaledHeight = aspectHeight * scaleFactor;

        return { width: scaledWidth, height: scaledHeight };
    };

    const { width: scaledWidth, height: scaledHeight } = calculateScaledDimensions()
    
    // Helper to calculate area rect in pixels
    const getAreaRect = (areaItem) => {
        const startMargin = parseFloat(areaItem.start_margin) || 0;
        const topMargin = parseFloat(areaItem.top_margin) || 0;
        const widthVal = parseFloat(areaItem.width) || 0;
        const heightVal = parseFloat(areaItem.height) || 0;

        const x = unit.value === "%"
            ? (startMargin / 100) * (scaledWidth || 1)
            : (startMargin / aspectWidth) * (scaledWidth || 1);
        const y = unit.value === "%"
            ? (topMargin / 100) * (scaledHeight || 1)
            : (topMargin / aspectHeight) * (scaledHeight || 1);
        const w = unit.value === "%"
            ? (widthVal / 100) * (scaledWidth || 1)
            : ((widthVal * 100) / aspectWidth / 100) * (scaledWidth || 1);
        const h = unit.value === "%"
            ? (heightVal / 100) * (scaledHeight || 1)
            : ((heightVal * 100) / aspectHeight / 100) * (scaledHeight || 1);
        
        return { x, y, w, h };
    };

    const SNAP_THRESHOLD = unit.value === "px" ? 10 : 1; //the too close limit

    const checkNearbyAreas = (currentArea, areas, newX, newY, newWidth, newHeight, action) => {
        areas.forEach(area => {
            if (area.id === currentArea.id) return;

            //   (R2L)    
            if (Math.abs(newX + newWidth - area.start_margin) < SNAP_THRESHOLD) {
                newWidth = area.start_margin - newX;
            }
            // (L2R) 
            if (Math.abs(newX - (area.start_margin + area.width)) < SNAP_THRESHOLD) {
                let delta = (area.start_margin + area.width) - newX;
                newX = area.start_margin + area.width;
                newWidth -= delta;
            }
            // (B2T)  
            if (Math.abs(newY + newHeight - area.top_margin) < SNAP_THRESHOLD) {
                newHeight = area.top_margin - newY;
            }
            //   (T2B) 
            if (Math.abs(newY - (area.top_margin + area.height)) < SNAP_THRESHOLD) {
                let delta = (area.top_margin + area.height) - newY;
                newY = area.top_margin + area.height;
                newHeight -= delta;
            }
        });

        return { newX, newY, newWidth, newHeight };
    };

    const handleMouseDown = (event, areaId) => {
        event.preventDefault();
        event.stopPropagation();
        const startX = event.clientX;
        const startY = event.clientY;

        setAreas(prevAreas =>
            prevAreas.map(area => {
                if (area.id !== areaId) return area;

                const div = document.getElementById(`resizable-${area.id}`);
                if (!div) return area;

                const rect = div.getBoundingClientRect();
                const edgeThreshold = 10;

                const nearLeft = startX - rect.left < edgeThreshold;
                const nearRight = rect.right - startX < edgeThreshold;
                const nearTop = startY - rect.top < edgeThreshold;
                const nearBottom = rect.bottom - startY < edgeThreshold;

                // Determine action
                let action = 'move';
                
                // Check if we clicked a specific move handle (like the ghost badge)
                // If so, FORCE move action and skip resizing checks
                // Check priority: Resize handles inside move handles should take precedence
                const resizeHandle = event.target.closest('[data-resize-handle]');
                const isMoveHandle = event.target.closest('[data-move-handle="true"]');
                
                if (resizeHandle) {
                    // Force resize action from the handle
                    action = resizeHandle.getAttribute('data-resize-handle');
                } else if (isMoveHandle) {
                   action = 'move';
                } else {
                    if (nearLeft) action = 'resizeLeft';
                    if (nearRight) action = 'resizeRight';
                    if (nearTop) action = 'resizeTop';
                    if (nearBottom) action = 'resizeBottom';
                }

                const handleMouseMove = (moveEvent) => {
                    let dx = (moveEvent.clientX - startX) / zoomLevel;
                    let dy = (moveEvent.clientY - startY) / zoomLevel;

                    let newX = area.start_margin;
                    let newY = area.top_margin;
                    let newWidth = area.width;
                    let newHeight = area.height;

                    const speedFactor = 2;
                    if (unit.value === "px") {
                        dx *= speedFactor;
                        dy *= speedFactor;

                        if (action === 'resizeRight') {
                            newWidth = Math.min(aspectWidth - area.start_margin, area.width + dx);
                        } else if (action === 'resizeLeft') {
                            let tempX = area.start_margin + dx;
                            let tempWidth = area.width - dx;

                            if (tempX < 0) {
                                tempWidth = area.width + area.start_margin;
                                tempX = 0;
                            }

                            if (tempWidth >= 5) {
                                newX = tempX;
                                newWidth = Math.min(aspectWidth - tempX, tempWidth);
                            }
                        } else if (action === 'resizeBottom') {
                            newHeight = Math.min(aspectHeight - area.top_margin, area.height + dy);
                        } else if (action === 'resizeTop') {
                            let tempY = area.top_margin + dy;
                            let tempHeight = area.height - dy;
                            if (tempY < 0) {
                                tempHeight = area.height + area.top_margin;
                                tempY = 0;
                            }
                            if (tempHeight >= 5) {
                                newY = tempY;
                                newHeight = Math.min(aspectHeight - tempY, tempHeight);
                            }
                        } else if (action === 'move') {
                            newX = Math.max(0, Math.min(aspectWidth - area.width, area.start_margin + dx));
                            newY = Math.max(0, Math.min(aspectHeight - area.height, area.top_margin + dy));
                        }
                    } else {
                        if (action === 'resizeRight') {
                            newWidth = Math.min(100 - area.start_margin, area.width + (dx / scaledWidth) * 100);
                        } else if (action === 'resizeLeft') {
                            let tempX = area.start_margin + (dx / scaledWidth) * 100;
                            let tempWidth = area.width - (dx / scaledWidth) * 100;

                            if (tempX < 0) {
                                tempWidth = area.width + area.start_margin;
                                tempX = 0;
                            }
                            if (tempWidth >= 5) {
                                newX = tempX;
                                newWidth = Math.min(100 - tempX, tempWidth);
                            }
                        } else if (action === 'resizeBottom') {
                            newHeight = Math.min(100 - area.top_margin, area.height + (dy / scaledHeight) * 100);
                        } else if (action === 'resizeTop') {
                            let tempY = area.top_margin + (dy / scaledHeight) * 100;
                            let tempHeight = area.height - (dy / scaledHeight) * 100;
                            if (tempY < 0) {
                                tempHeight = area.height + area.top_margin;
                                tempY = 0;
                            }
                            if (tempHeight >= 5) {
                                newY = tempY;
                                newHeight = Math.min(100 - tempY, tempHeight);
                            }
                        } else if (action === 'move') {
                            newX = Math.max(0, Math.min(100 - area.width, area.start_margin + (dx / scaledWidth) * 100));
                            newY = Math.max(0, Math.min(100 - area.height, area.top_margin + (dy / scaledHeight) * 100));
                        }
                    }

                    //  snapping logic
                    const snappedValues = checkNearbyAreas(area, prevAreas, newX, newY, newWidth, newHeight);
                    newX = snappedValues.newX;
                    newY = snappedValues.newY;
                    newWidth = snappedValues.newWidth;
                    newHeight = snappedValues.newHeight;

                    setAreas(prev =>
                        prev.map(a =>
                            a.id === areaId
                                ? { ...a, start_margin: newX, top_margin: newY, width: newWidth, height: newHeight }
                                : a
                        )
                    );
                };




                const handleMouseUp = () => {
                    document.removeEventListener("mousemove", handleMouseMove);
                    document.removeEventListener("mouseup", handleMouseUp);
                };

                document.addEventListener("mousemove", handleMouseMove);
                document.addEventListener("mouseup", handleMouseUp);

                return area;
            })
        );
    };

    const handleOnClick = (e, areaId, area) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        dispatch(setSelectedArea(area))

        if (scrollableDivRef.current) {
            // Find the element with the selected id inside the scrollable div
            const relatedElement = scrollableDivRef.current.querySelector(`#${areaId}`);
            if (relatedElement) {
                // Scroll to the related element
                relatedElement.scrollIntoView({ behavior: "smooth", block: "start" });
            }
        }

    }
    return (
        <> {
            isFetching ?
                <div className="col-12 d-flex justify-content-center p-4 border-top border-gray">
                    <Spinner as="span" variant="primary" size='lg' animation="border" role="status" className='mx-3 my-5' aria-hidden="true" />
                </div>
                :
                <div className="w-full h-full flex flex-col items-center relative z-10">
                    <div className={`w-full max-w-[1200px] flex items-center justify-between bg-white p-3 rounded-xl shadow-sm border border-gray-200/60 backdrop-blur-sm bg-opacity-90 transition-all duration-300 ${
                        isCanvasFullscreen ? 'mt-2 mb-2' : 'mb-6'
                    }`}>
                        <div className="flex-1 pr-4 border-r border-gray-100">
                             <AspectRatioController areas={areas} setAreas={setAreas} aspectRatios={aspectRatios} currentAspectRatio={currentAspectRatio} setCurrentAspectRatio={setCurrentAspectRatio} setAspectRatios={setAspectRatios} />
                        </div>
                        <div className="pl-4">
                             <AddArea currentAspectRatio={currentAspectRatio} setAreas={setAreas} areas={areas} setAreaColor={setAreaColor} setAreaOpacity={setAreaOpacity} />
                        </div>
                    </div>

                    <div 
                        id="theDrawingArea" 
                        ref={drawingAreaRef} 
                        className={`relative w-full flex-1 flex justify-center items-center transition-transform duration-200 ease-out origin-top ${
                            isCanvasFullscreen ? 'max-w-none p-1' : 'max-w-[1200px] p-4'
                        }`}
                        style={{ transform: `scale(${zoomLevel})` }}
                    >


                        <div 
                            className={`flex items-center justify-center relative transition-all duration-300 ${
                                    isCanvasFullscreen 
                                    ? '' 
                                    : 'bg-slate-900 shadow-[0_25px_60px_-12px_rgba(0,0,0,0.6)] rounded-3xl ring-[10px] ring-slate-800'
                            }`} 
                            style={{ width: scaledWidth, height: scaledHeight }}
                        >
                            <div 
                                id={"outLine"} 
                                onClick={() => dispatch(setSelectedArea({}))}
                                className={`overflow-visible relative ${
                                    isCanvasFullscreen 
                                        ? '' 
                                        : 'bg-slate-50 rounded-xl border-2 border-dashed border-gray-300/50'
                                }`} 
                                style={{ 
                                    cursor: 'default', 
                                    width: scaledWidth,
                                    height: scaledHeight,
                                    ...(() => {
                                        const pattern = toolsState?.backgroundPattern;
                                        switch (pattern) {
                                            case 'dots':
                                                return {
                                                    backgroundImage: 'radial-gradient(#cbd5e1 1.5px, transparent 1.5px)',
                                                    backgroundSize: '20px 20px'
                                                };
                                            case 'lines':
                                                return {
                                                    backgroundImage: 'repeating-linear-gradient(45deg, #cbd5e1 0, #cbd5e1 1px, transparent 0, transparent 10px)'
                                                };
                                            case 'checkerboard':
                                                return {
                                                    backgroundImage: `linear-gradient(45deg, #e2e8f0 25%, transparent 25%), linear-gradient(-45deg, #e2e8f0 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #e2e8f0 75%), linear-gradient(-45deg, transparent 75%, #e2e8f0 75%)`,
                                                    backgroundSize: '20px 20px',
                                                    backgroundPosition: '0 0, 0 10px, 10px -10px, -10px 0px'
                                                };
                                            default:
                                                return {};
                                        }
                                    })()
                                }}
                            >
                                {/* Crosshair Overlay */}
                                {toolsState?.crosshair?.enabled && (
                                    <CrosshairOverlay
                                        enabled={toolsState.crosshair.enabled}
                                        width={scaledWidth}
                                        height={scaledHeight}
                                        color={toolsState.crosshair.color}
                                    />
                                )}

                                {/* Rulers Overlay */}
                                {toolsState?.rulers?.enabled && (
                                    <RulersOverlay
                                        enabled={toolsState.rulers.enabled}
                                        color={toolsState.rulers.color}
                                        width={scaledWidth}
                                        height={scaledHeight}
                                        unit={toolsState.rulers.unit}
                                        aspectRatio={currentAspectRatio}
                                    />
                                )}
                                
                                {/* Grid Overlay */}
                                {toolsState?.grid?.enabled && (
                                    <GridOverlay
                                        enabled={toolsState.grid.enabled}
                                        size={toolsState.grid.size}
                                        color={toolsState.grid.color}
                                        opacity={toolsState.grid.opacity}
                                        width={scaledWidth}
                                        height={scaledHeight}
                                    />
                                )}

                                {/* Grayscale Filter Layer */}
                                {toolsState?.grayscale?.enabled && (
                                    <div className="absolute inset-0 pointer-events-none z-[100] bg-transparent backdrop-grayscale" style={{ backdropFilter: 'grayscale(100%)' }}></div>
                                )}

                                {/* Alignment Guides */}
                                {toolsState?.alignmentGuides?.enabled && (
                                    <AlignmentGuides
                                        enabled={toolsState.alignmentGuides.enabled}
                                        color={toolsState.alignmentGuides.color}
                                        opacity={toolsState.alignmentGuides.opacity}
                                        width={scaledWidth}
                                        height={scaledHeight}
                                        guides={[]}
                                    />
                                )}

                                {/* Safe Zones */}
                                {toolsState?.safeZones?.enabled && (
                                    <SafeZones
                                        enabled={toolsState.safeZones.enabled}
                                        margin={toolsState.safeZones.margin}
                                        color={toolsState.safeZones.color}
                                        opacity={toolsState.safeZones.opacity}
                                        width={scaledWidth}
                                        height={scaledHeight}
                                    />
                                )}
                                {(() => {
                                    const showOutlineOnly = toolsState?.outlineMode?.enabled;
                                    const showAreaNumbers = toolsState?.areaNumbers?.visible !== false;

                                    return (
                                        <>
                                            {areas.map((area, index) => {
                                                const customColor = toolsState?.areaColors?.[area.id];
                                                const customOpacity = toolsState?.areaOpacity?.[area.id];
                                    
                                    // Focus Mode Logic
                                    const isFocused = selectedArea?.id === area.id;
                                    const focusModeEnabled = toolsState?.focusMode?.enabled;
                                    const focusOpacity = toolsState?.focusMode?.opacity || 0.3;
                                    const dimStyle = focusModeEnabled && !isFocused ? { 
                                        opacity: focusOpacity, 
                                        filter: 'grayscale(100%) blur(1px)',
                                        transition: 'opacity 0.3s ease, filter 0.3s ease, border-color 0.3s ease, background-color 0.3s ease'
                                    } : {
                                        transition: 'opacity 0.3s ease, filter 0.3s ease, border-color 0.3s ease, background-color 0.3s ease'
                                    };
                                    
                                    // Border Logic
                                    const borderStyle = toolsState?.areaBorder?.style || 'solid';
                                    const borderWidth = toolsState?.areaBorder?.width !== undefined ? toolsState.areaBorder.width : 1;
                                    const isSelected = selectedArea?.id === area.id;
                                    const borderColor = isSelected ? '#60a5fa' : (toolsState?.areaBorder?.color || '#3b82f6'); // Brighter blue when selected
                                    
                                    // Resize Handles Logic
                                    const showHandles = toolsState?.resizeHandles?.visible !== false && selectedArea?.id === area.id;
                                    const handleShape = toolsState?.resizeHandles?.shape || 'square';
                                    const handleSize = toolsState?.resizeHandles?.size || 8;
                                    const handleColor = toolsState?.resizeHandles?.color || '#ffffff';
                                    const handleBorderColor = toolsState?.resizeHandles?.borderColor || '#3b82f6';
                                    
                                    // Calculate Radius
                                    let handleRadius = '2px';
                                    if (handleShape === 'circle') handleRadius = '50%';
                                    if (handleShape === 'rounded') handleRadius = '4px';
                                    if (handleShape === 'pill') handleRadius = '10px';

                                    // Base Handle Style
                                    const baseHandleStyle = {
                                        backgroundColor: handleColor,
                                        border: `1px solid ${handleBorderColor}`,
                                        borderRadius: handleRadius,
                                        zIndex: 50,
                                        position: 'absolute',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        boxShadow: "0 2px 4px rgba(0,0,0,0.15)"
                                    };

                                    return (
                                    <div
                                        id={`resizable-${area.id}`}
                                        key={`resizable-${area.id}`}
                                        className="absolute cursor-pointer resizable"
                                        onClick={(e) => handleOnClick(e, `area_${area.id}`, area)}
                                        onMouseDown={(e) => handleMouseDown(e, area.id)}
                                        style={{
                                            // Invert order: lower order = higher z-index (on top)
                                            // order=1 gets highest z-index, order=N gets lowest
                                            zIndex: area.order ? (areas.length - area.order + 1) : (areas.length - index), 
                                            ...dimStyle,
                                            borderStyle: borderStyle,
                                            borderWidth: `${borderWidth}px`,
                                            borderColor: borderColor,
                                            left: unit.value === "%"
                                                ? `${((area.start_margin ?? 0) / 100) * (scaledWidth || 1)}px`
                                                : `${((area.start_margin ?? 0) / aspectWidth) * (scaledWidth || 1)}px`,
                                            top: unit.value === "%"
                                                ? `${((area.top_margin ?? 0) / 100) * (scaledHeight || 1)}px`
                                                : `${((area.top_margin ?? 0) / aspectHeight) * (scaledHeight || 1)}px`,
                                            width: unit.value === "%"
                                                ? `${area.width}%`
                                                : `${(area.width * 100) / aspectWidth}%`,
                                            height: unit.value === "%"
                                                ? `${area.height}%`
                                                : `${(area.height * 100) / aspectHeight}%`,
                                            backgroundColor: (() => {
                                                const baseColor = customColor 
                                                    ? customColor
                                                    : selectedArea?.id === area.id ? "rgba(96, 165, 250, 0.3)" : layoutColor[index];
                                                
                                                // Apply customOpacity to the background color instead of using CSS opacity property
                                                // This prevents content from becoming transparent
                                                if (customOpacity !== undefined && customOpacity !== 1) {
                                                    // Convert color to rgba with opacity
                                                    if (baseColor.startsWith('rgba')) {
                                                        // Already rgba, extract and modify alpha
                                                        const match = baseColor.match(/rgba\((\d+),\s*(\d+),\s*(\d+),\s*([0-9.]+)\)/);
                                                        if (match) {
                                                            return `rgba(${match[1]}, ${match[2]}, ${match[3]}, ${customOpacity})`;
                                                        }
                                                    } else if (baseColor.startsWith('rgb')) {
                                                        // rgb to rgba
                                                        const match = baseColor.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
                                                        if (match) {
                                                            return `rgba(${match[1]}, ${match[2]}, ${match[3]}, ${customOpacity})`;
                                                        }
                                                    } else if (baseColor.startsWith('#')) {
                                                        // hex to rgba
                                                        const hex = baseColor.replace('#', '');
                                                        const r = parseInt(hex.substring(0, 2), 16);
                                                        const g = parseInt(hex.substring(2, 4), 16);
                                                        const b = parseInt(hex.substring(4, 6), 16);
                                                        return `rgba(${r}, ${g}, ${b}, ${customOpacity})`;
                                                    }
                                                }
                                                return baseColor;
                                            })(),
                                            // Add diagonal stripes if this area is behind another area
                                            backgroundImage: (() => {
                                                // Check if this area is overlapped by any area with lower order (higher z-index)
                                                const currentRect = getAreaRect(area);
                                                const isOverlapped = areas.some(otherArea => {
                                                    if (otherArea.id === area.id) return false;
                                                    // Check if otherArea is in front (lower order value = higher z-index)
                                                    if ((otherArea.order || 999) >= (area.order || 999)) return false;
                                                    
                                                    const otherRect = getAreaRect(otherArea);
                                                    const x_overlap = Math.max(0, Math.min(currentRect.x + currentRect.w, otherRect.x + otherRect.w) - Math.max(currentRect.x, otherRect.x));
                                                    const y_overlap = Math.max(0, Math.min(currentRect.y + currentRect.h, otherRect.y + otherRect.h) - Math.max(currentRect.y, otherRect.y));
                                                    return x_overlap > 1 && y_overlap > 1;
                                                });
                                                
                                                if (isOverlapped) {
                                                    const bgColor = customColor || (selectedArea?.id === area.id ? "rgba(96, 165, 250, 0.3)" : layoutColor[index]);
                                                    // Generate unique angle and stripe color based on index to differentiate areas
                                                    const angle = (45 + (index * 60)) % 180; 
                                                    const stripeColors = [
                                                        'rgba(255, 255, 255, 0.15)', // White
                                                        'rgba(168, 85, 247, 0.15)',  // Purple
                                                        'rgba(34, 197, 94, 0.15)',   // Green
                                                        'rgba(249, 115, 22, 0.15)',  // Orange
                                                        'rgba(239, 68, 68, 0.15)',   // Red
                                                        'rgba(6, 182, 212, 0.15)',   // Cyan
                                                    ];
                                                    const stripeColor = stripeColors[index % stripeColors.length];
                                                    return `repeating-linear-gradient(${angle}deg, ${bgColor}, ${bgColor} 10px, ${stripeColor} 10px, ${stripeColor} 20px)`;
                                                }
                                                return 'none';
                                            })(),
                                            // Removed opacity property - now applied directly to backgroundColor
                                            display: "flex",
                                            alignItems: "stretch",
                                            justifyContent: "center",
                                            textAlign: "center",
                                            fontSize: "14px",
                                            color: "black",
                                            position: "absolute",
                                        }}
                                    >
                                        {/* Content wrapper with conditional pointer-events */}
                                        <div style={{
                                            width: '100%',
                                            height: '100%',
                                            // Disable pointer events on content to facilitate dragging
                                            // The parent container handles all interactions (selection, dragging/resizing)
                                            pointerEvents: 'none'
                                        }}>
                                            {!showOutlineOnly && <VisualAreaContent areaId={area.id} settings={area.settings?.live} showTimeline={toolsState?.timeline?.visible !== false} isPaused={isPaused} />}
                                        </div>
                                        


                                    </div>
                                    );
                                })}

                                {/* Selection Overlay */}
                                {selectedArea && (() => {
                                    const area = areas.find(a => a.id === selectedArea.id);
                                    if (!area) return null;

                                    const handleShape = toolsState?.resizeHandles?.shape || 'square';
                                    const handleSize = toolsState?.resizeHandles?.size || 8;
                                    const handleColor = toolsState?.resizeHandles?.color || '#ffffff';
                                    const handleBorderColor = toolsState?.resizeHandles?.borderColor || '#3b82f6';

                                    let handleRadius = '2px';
                                    if (handleShape === 'circle') handleRadius = '50%';
                                    if (handleShape === 'rounded') handleRadius = '4px';
                                    if (handleShape === 'pill') handleRadius = '10px';

                                    const baseHandleStyle = {
                                        backgroundColor: handleColor,
                                        border: `1px solid ${handleBorderColor}`,
                                        borderRadius: handleRadius,
                                        zIndex: 2601, // Highly elevated, above the border within the same context
                                        position: 'absolute',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        boxShadow: "0 2px 4px rgba(0,0,0,0.15)"
                                    };

                                    return (
                                        <div
                                            style={{
                                                position: 'absolute',
                                                left: unit.value === "%"
                                                    ? `${((area.start_margin ?? 0) / 100) * (scaledWidth || 1)}px`
                                                    : `${((area.start_margin ?? 0) / aspectWidth) * (scaledWidth || 1)}px`,
                                                top: unit.value === "%"
                                                    ? `${((area.top_margin ?? 0) / 100) * (scaledHeight || 1)}px`
                                                    : `${((area.top_margin ?? 0) / aspectHeight) * (scaledHeight || 1)}px`,
                                                width: unit.value === "%"
                                                    ? `${area.width}%`
                                                    : `${(area.width * 100) / aspectWidth}%`,
                                                height: unit.value === "%"
                                                    ? `${area.height}%`
                                                    : `${(area.height * 100) / aspectHeight}%`,
                                                // Match the selected area's z-index + 0.1 for border/handles
                                                zIndex: 2600, // Elevate above selected area
                                                pointerEvents: 'none', // Allow clicks to pass through except for handles/border
                                            }}
                                        >
                                            {/* White Professional Border */}
                                            <div
                                                onMouseDown={(e) => {
                                                    // Only start drag if clicking the border itself (simulated by inner content blocking clicks)
                                                    // In this case, we wrap the border in a pointer-events-auto div
                                                    handleMouseDown(e, area.id);
                                                }}
                                                className="absolute inset-0 cursor-move"
                                                style={{
                                                    border: `1px solid ${handleBorderColor}`,
                                                    pointerEvents: 'auto', // Enable interaction
                                                    backgroundColor: 'transparent'
                                                }}
                                            />

                                            {/* Resize Handles */}
                                            {/* Left Handle (Vertical Pill) */}
                                            <div
                                                className="resize-handle pointer-events-auto"
                                                style={{
                                                    ...baseHandleStyle,
                                                    width: handleShape === 'pill' ? handleSize / 2 : handleSize,
                                                    height: handleShape === 'pill' ? handleSize * 3 : handleSize,
                                                    top: "50%",
                                                    marginTop: handleShape === 'pill' ? -(handleSize * 1.5) : -handleSize / 2,
                                                    left: handleShape === 'pill' ? -handleSize / 4 : -handleSize / 2,
                                                    cursor: "ew-resize"
                                                }}
                                                onMouseDown={(e) => handleMouseDown(e, area.id)}
                                            />
                                            {/* Top Handle (Horizontal Pill) */}
                                            <div
                                                className="resize-handle pointer-events-auto"
                                                style={{
                                                    ...baseHandleStyle,
                                                    width: handleShape === 'pill' ? handleSize * 3 : handleSize,
                                                    height: handleShape === 'pill' ? handleSize / 2 : handleSize,
                                                    left: "50%",
                                                    marginLeft: handleShape === 'pill' ? -(handleSize * 1.5) : -handleSize / 2,
                                                    top: handleShape === 'pill' ? -handleSize / 4 : -handleSize / 2,
                                                    cursor: "ns-resize"
                                                }}
                                                onMouseDown={(e) => handleMouseDown(e, area.id)}
                                            />
                                            {/* Bottom Handle (Horizontal Pill) */}
                                            <div
                                                className="resize-handle pointer-events-auto"
                                                style={{
                                                    ...baseHandleStyle,
                                                    width: handleShape === 'pill' ? handleSize * 3 : handleSize,
                                                    height: handleShape === 'pill' ? handleSize / 2 : handleSize,
                                                    left: "50%",
                                                    marginLeft: handleShape === 'pill' ? -(handleSize * 1.5) : -handleSize / 2,
                                                    bottom: handleShape === 'pill' ? -handleSize / 4 : -handleSize / 2,
                                                    cursor: "ns-resize"
                                                }}
                                                onMouseDown={(e) => handleMouseDown(e, area.id)}
                                            />
                                            {/* Right Handle (Vertical Pill) */}
                                            <div
                                                className="resize-handle pointer-events-auto"
                                                style={{
                                                    ...baseHandleStyle,
                                                    width: handleShape === 'pill' ? handleSize / 2 : handleSize,
                                                    height: handleShape === 'pill' ? handleSize * 3 : handleSize,
                                                    top: "50%",
                                                    marginTop: handleShape === 'pill' ? -(handleSize * 1.5) : -handleSize / 2,
                                                    right: handleShape === 'pill' ? -handleSize / 4 : -handleSize / 2,
                                                    cursor: "ew-resize"
                                                }}
                                                onMouseDown={(e) => handleMouseDown(e, area.id)}
                                            />
                                        </div>
                                    );

                                })()}

                                {/* Smart Overlap Ghosts */}
                                {(() => {
                                    // Helper to get pixel rect for an area
                                    // Helper to get pixel rect for an area
                                    const getAreaRect = (area) => {
                                        const startMargin = parseFloat(area.start_margin) || 0;
                                        const topMargin = parseFloat(area.top_margin) || 0;
                                        const widthVal = parseFloat(area.width) || 0;
                                        const heightVal = parseFloat(area.height) || 0;

                                        const x = unit.value === "%"
                                            ? (startMargin / 100) * (scaledWidth || 1)
                                            : (startMargin / aspectWidth) * (scaledWidth || 1);
                                        const y = unit.value === "%"
                                            ? (topMargin / 100) * (scaledHeight || 1)
                                            : (topMargin / aspectHeight) * (scaledHeight || 1);
                                        const w = unit.value === "%"
                                            ? (widthVal / 100) * (scaledWidth || 1)
                                            : ((widthVal * 100) / aspectWidth / 100) * (scaledWidth || 1);
                                        const h = unit.value === "%"
                                            ? (heightVal / 100) * (scaledHeight || 1)
                                            : ((heightVal * 100) / aspectHeight / 100) * (scaledHeight || 1);
                                        return { x, y, w, h };
                                    };

                                    const ghosts = [];
                                    
                                    // Check all pairs of areas for overlaps
                                    // Area with LOWER order is in FRONT (higher z-index)
                                    for (let i = 0; i < areas.length; i++) {
                                        for (let j = i + 1; j < areas.length; j++) {
                                            const areaA = areas[i];
                                            const areaB = areas[j];
                                            
                                            // Calculate z-index: invert order (lower order = higher z-index)
                                            const zIndexA = areaA.order ? (areas.length - areaA.order + 1) : (areas.length - i);
                                            const zIndexB = areaB.order ? (areas.length - areaB.order + 1) : (areas.length - j);
                                            
                                            const topArea = zIndexA > zIndexB ? areaA : areaB;
                                            const bottomArea = zIndexA > zIndexB ? areaB : areaA;
                                            
                                            const topRect = getAreaRect(topArea);
                                            const bottomRect = getAreaRect(bottomArea);

                                            // Check for intersection
                                            const x_overlap = Math.max(0, Math.min(bottomRect.x + bottomRect.w, topRect.x + topRect.w) - Math.max(bottomRect.x, topRect.x));
                                            const y_overlap = Math.max(0, Math.min(bottomRect.y + bottomRect.h, topRect.y + topRect.h) - Math.max(bottomRect.y, topRect.y));
                                            const isOverlapping = x_overlap > 1 && y_overlap > 1; // Tolerance

                                            // Create ghost for bottom area when there is actual overlap
                                            // Only show when NEITHER the front area NOR the back area is selected
                                            // Create ghost for bottom area when there is actual overlap
                                            // ALWAYS show ghost for hidden areas, but make it "hollow" so clicks pass through center
                                            // Only the yellow border itself will be interactive
                                            if (isOverlapping && zIndexA > zIndexB) {
                                                const behindRect = getAreaRect(areaB);
                                                
                                                // Interactive border layer (above everything)
                                                ghosts.push(
                                                    <div
                                                        key={`ghost-${areaB.id}-under-${areaA.id}`}
                                                        className="cursor-move"
                                                        title={`Hidden: Area ${areaB.id} (Order: ${areaB.order})`}
                                                        style={{
                                                            position: 'absolute',
                                                            left: `${behindRect.x}px`,
                                                            top: `${behindRect.y}px`,
                                                            width: `${behindRect.w}px`,
                                                            height: `${behindRect.h}px`,
                                                            zIndex: selectedArea?.id === areaB.id ? 2600 : 1004, // Elevate active ghost above all inactive markers
                                                            pointerEvents: selectedArea?.id === areaB.id ? 'auto' : 'none', // Hollow by default, solid when selected!
                                                            backgroundColor: 'transparent'
                                                        }}
                                                        data-move-handle="true" // Allow dragging from center when active
                                                        onMouseDown={(e) => handleMouseDown(e, areaB.id)}
                                                    >
                                                        {(() => {
                                                            const isGhostSelected = selectedArea?.id === areaB.id;
                                                            const ghostBorderColor = isGhostSelected ? '#facc15' : 'rgba(255, 255, 255, 0.8)'; // Distinct White
                                                            const ghostShadow = isGhostSelected ? '0 0 15px rgba(250, 204, 21, 0.6)' : '0 0 10px rgba(255, 255, 255, 0.3)';
                                                            
                                                            return (
                                                                <>
                                                                    {/* 4 Separate Border Sides that ARE clickable */}
                                                                    {/* Top Border */}
                                                                    <div 
                                                                        style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', backgroundColor: ghostBorderColor, boxShadow: ghostShadow, pointerEvents: 'auto', cursor: 'pointer' }}
                                                                        onMouseDown={(e) => handleMouseDown(e, areaB.id)}
                                                                        onClick={(e) => handleOnClick(e, `area_${areaB.id}`, areaB)}
                                                                    />
                                                                    {/* Bottom Border */}
                                                                    <div 
                                                                        style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '3px', backgroundColor: ghostBorderColor, boxShadow: ghostShadow, pointerEvents: 'auto', cursor: 'pointer' }}
                                                                        onMouseDown={(e) => handleMouseDown(e, areaB.id)}
                                                                        onClick={(e) => handleOnClick(e, `area_${areaB.id}`, areaB)}
                                                                    />
                                                                    {/* Left Border */}
                                                                    <div 
                                                                        style={{ position: 'absolute', top: 0, bottom: 0, left: 0, width: '3px', backgroundColor: ghostBorderColor, boxShadow: ghostShadow, pointerEvents: 'auto', cursor: 'pointer' }}
                                                                        onMouseDown={(e) => handleMouseDown(e, areaB.id)}
                                                                        onClick={(e) => handleOnClick(e, `area_${areaB.id}`, areaB)}
                                                                    />
                                                                    {/* Right Border */}
                                                                    <div 
                                                                        style={{ position: 'absolute', top: 0, bottom: 0, right: 0, width: '3px', backgroundColor: ghostBorderColor, boxShadow: ghostShadow, pointerEvents: 'auto', cursor: 'pointer' }}
                                                                        onMouseDown={(e) => handleMouseDown(e, areaB.id)}
                                                                        onClick={(e) => handleOnClick(e, `area_${areaB.id}`, areaB)}
                                                                    />
                                                                </>
                                                            );
                                                        })()}

                                                        {/* Floating Badge Handle - Professional Design */}
                                                        {(() => {
                                                            const overlapArea = x_overlap * y_overlap;
                                                            const totalArea = bottomRect.w * bottomRect.h;
                                                            const coverageRatio = totalArea > 0 ? (overlapArea / totalArea) : 0;
                                                            
                                                            if (coverageRatio > 0.7) {
                                                                return (
                                                                    <div
                                                                        data-move-handle="true" // Force move action!
                                                                        style={{
                                                                            position: 'absolute',
                                                                            top: '0%', // Position on top edge
                                                                            left: '50%', // Centered horizontally
                                                                            transform: 'translate(-50%, -140%)', // Moved completely ABOVE the line
                                                                            marginTop: '-5px', // Extra spacing from the line
                                                                            // Conditional styling based on selection
                                                                            background: selectedArea?.id === areaB.id 
                                                                                ? 'rgba(250, 204, 21, 0.9)'  // Active: Yellow with transparency
                                                                                : 'rgba(255, 255, 255, 0.7)',   // Inactive: Distinct White!
                                                                            backdropFilter: 'blur(4px)',     // Glass effect
                                                                            color: '#000', // Black text for both yellow and white backgrounds
                                                                            padding: '6px 14px', // Slightly smaller for edge position
                                                                            borderRadius: '9999px', // Pill shape
                                                                            fontSize: '13px',
                                                                            fontWeight: '700',
                                                                            fontFamily: 'ui-sans-serif, system-ui, sans-serif',
                                                                            boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2), 0 2px 4px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.4)', // Deep shadow + Highlight
                                                                            pointerEvents: 'auto',
                                                                            cursor: 'pointer',
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            gap: '6px',
                                                                            zIndex: 206,
                                                                            whiteSpace: 'nowrap',
                                                                            border: '1px solid rgba(255, 255, 255, 0.4)',
                                                                            transition: 'transform 0.2s cubic-bezier(0.34, 1.56, 0.64, 1)',
                                                                        }}
                                                                        onMouseEnter={(e) => e.currentTarget.style.transform = 'translate(-50%, -140%) scale(1.05)'}
                                                                        onMouseLeave={(e) => e.currentTarget.style.transform = 'translate(-50%, -140%) scale(1)'}
                                                                        onMouseDown={(e) => handleMouseDown(e, areaB.id)}
                                                                        onClick={(e) => handleOnClick(e, `area_${areaB.id}`, areaB)}
                                                                        title="Click to select hidden area"
                                                                    >
                                                                        {/* Compact: number badge + drag icon */}
                                                                        <span style={{
                                                                            display: 'inline-flex',
                                                                            alignItems: 'center',
                                                                            justifyContent: 'center',
                                                                            width: '18px',
                                                                            height: '18px',
                                                                            borderRadius: '50%',
                                                                            background: selectedArea?.id === areaB.id ? 'rgba(0,0,0,0.25)' : 'rgba(0,0,0,0.15)',
                                                                            fontSize: '10px',
                                                                            fontWeight: '900',
                                                                            lineHeight: 1,
                                                                        }}>
                                                                            {areaB.order ?? '?'}
                                                                        </span>
                                                                        {/* 4-directional move icon */}
                                                                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.75 }}>
                                                                            <polyline points="5 9 2 12 5 15" />
                                                                            <polyline points="9 5 12 2 15 5" />
                                                                            <polyline points="15 19 12 22 9 19" />
                                                                            <polyline points="19 9 22 12 19 15" />
                                                                            <line x1="2" y1="12" x2="22" y2="12" />
                                                                            <line x1="12" y1="2" x2="12" y2="22" />
                                                                        </svg>
                                                                    </div>
                                                                );
                                                            }
                                                            return null;
                                                        })()}
                                                                {/* Number badge removed - using area's own number */}


                                                                {/* Render Handles for the Ghost */}
                                                                {(() => {
                                                                    const handleShape = toolsState?.resizeHandles?.shape || 'square';
                                                                    const handleSize = toolsState?.resizeHandles?.size || 8;
                                                                    const handleColor = toolsState?.resizeHandles?.color || '#ffffff';
                                                                    const handleBorderColor = (selectedArea?.id === areaB.id) 
                                                                        ? (toolsState?.resizeHandles?.borderColor || '#3b82f6') 
                                                                        : 'rgba(255, 255, 255, 0.8)'; // Distinct White for inactive handles

                                                                    let handleRadius = '2px';
                                                                    if (handleShape === 'circle') handleRadius = '50%';
                                                                    if (handleShape === 'rounded') handleRadius = '4px';
                                                                    if (handleShape === 'pill') handleRadius = '10px';

                                                                    const baseGhostHandleStyle = {
                                                                        backgroundColor: handleColor,
                                                                        border: `1px solid ${handleBorderColor}`,
                                                                        borderRadius: handleRadius,
                                                                        position: 'absolute',
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'center',
                                                                        boxShadow: "0 2px 4px rgba(0,0,0,0.15)",
                                                                        zIndex: 205, // Above the ghost border
                                                                        pointerEvents: 'auto' // Ensure handles are clickable!
                                                                    };

                                                                    return (
                                                                        <>
                                                                            {/* Left Handle */}
                                                                            <div
                                                                                data-resize-handle="resizeLeft" // Dedicated logic
                                                                                className="resize-handle pointer-events-auto"
                                                                                style={{
                                                                                    ...baseGhostHandleStyle,
                                                                                    width: handleShape === 'pill' ? handleSize / 2 : handleSize,
                                                                                    height: handleShape === 'pill' ? handleSize * 3 : handleSize,
                                                                                    top: "50%",
                                                                                    marginTop: handleShape === 'pill' ? -(handleSize * 1.5) : -handleSize / 2,
                                                                                    left: handleShape === 'pill' ? -handleSize / 4 : -handleSize / 2,
                                                                                    cursor: "ew-resize"
                                                                                }}
                                                                                onMouseDown={(e) => handleMouseDown(e, areaB.id)}
                                                                            />
                                                                            {/* Top Handle */}
                                                                            <div
                                                                                data-resize-handle="resizeTop" // Dedicated logic
                                                                                className="resize-handle pointer-events-auto"
                                                                                style={{
                                                                                    ...baseGhostHandleStyle,
                                                                                    width: handleShape === 'pill' ? handleSize * 3 : handleSize,
                                                                                    height: handleShape === 'pill' ? handleSize / 2 : handleSize,
                                                                                    left: "50%",
                                                                                    marginLeft: handleShape === 'pill' ? -(handleSize * 1.5) : -handleSize / 2,
                                                                                    top: handleShape === 'pill' ? -handleSize / 4 : -handleSize / 2,
                                                                                    cursor: "ns-resize"
                                                                                }}
                                                                                onMouseDown={(e) => handleMouseDown(e, areaB.id)}
                                                                            />
                                                                            {/* Bottom Handle */}
                                                                            <div
                                                                                data-resize-handle="resizeBottom" // Dedicated logic
                                                                                className="resize-handle pointer-events-auto"
                                                                                style={{
                                                                                    ...baseGhostHandleStyle,
                                                                                    width: handleShape === 'pill' ? handleSize * 3 : handleSize,
                                                                                    height: handleShape === 'pill' ? handleSize / 2 : handleSize,
                                                                                    left: "50%",
                                                                                    marginLeft: handleShape === 'pill' ? -(handleSize * 1.5) : -handleSize / 2,
                                                                                    bottom: handleShape === 'pill' ? -handleSize / 4 : -handleSize / 2,
                                                                                    cursor: "ns-resize"
                                                                                }}
                                                                                onMouseDown={(e) => handleMouseDown(e, areaB.id)}
                                                                            />
                                                                            {/* Right Handle */}
                                                                            <div
                                                                                data-resize-handle="resizeRight" // Dedicated logic
                                                                                className="resize-handle pointer-events-auto"
                                                                                style={{
                                                                                    ...baseGhostHandleStyle,
                                                                                    width: handleShape === 'pill' ? handleSize / 2 : handleSize,
                                                                                    height: handleShape === 'pill' ? handleSize * 3 : handleSize,
                                                                                    top: "50%",
                                                                                    marginTop: handleShape === 'pill' ? -(handleSize * 1.5) : -handleSize / 2,
                                                                                    right: handleShape === 'pill' ? -handleSize / 4 : -handleSize / 2,
                                                                                    cursor: "ew-resize"
                                                                                }}
                                                                                onMouseDown={(e) => handleMouseDown(e, areaB.id)}
                                                                            />
                                                                        </>
                                                                    );
                                                                })()}
                                                        </div>
                                                    );
                                                }
                                                
                                            }
                                        }
                                    return ghosts;
                                })()}

                                {/* Dedicated Label Layer (Ensures visibility above all areas) */}
                                <div className="absolute inset-0 pointer-events-none">
                                    {areas.map((area, index) => {
                                        const currentRect = getAreaRect(area);
                                        const areaStyle = {
                                            position: 'absolute',
                                            left: unit.value === "%"
                                                ? `${((area.start_margin ?? 0) / 100) * (scaledWidth || 1)}px`
                                                : `${((area.start_margin ?? 0) / aspectWidth) * (scaledWidth || 1)}px`,
                                            top: unit.value === "%"
                                                ? `${((area.top_margin ?? 0) / 100) * (scaledHeight || 1)}px`
                                                : `${((area.top_margin ?? 0) / aspectHeight) * (scaledHeight || 1)}px`,
                                            width: unit.value === "%"
                                                ? `${area.width}%`
                                                : `${(area.width * 100) / aspectWidth}%`,
                                            height: unit.value === "%"
                                                ? `${area.height}%`
                                                : `${(area.height * 100) / aspectHeight}%`,
                                        };

                                        return (
                                            <div key={`labels-${area.id}`} style={{ ...areaStyle, zIndex: selectedArea?.id === area.id ? 2700 : 1500 }} className="pointer-events-none">
                                                {/* Area Number */}
                                                {showAreaNumbers && (() => {
                                                    const position = toolsState?.areaNumbers?.position || 'center';
                                                    let positionClasses = '';
                                                    switch(position) {
                                                        case 'top': positionClasses = 'top-8 left-1/2 -translate-x-1/2'; break;
                                                        case 'bottom': positionClasses = 'bottom-12 left-1/2 -translate-x-1/2'; break;
                                                        case 'center':
                                                        default: positionClasses = 'top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2'; break;
                                                    }
                                                    return (
                                                        <div className={`absolute ${positionClasses} pointer-events-none`}>
                                                            <div className="w-12 h-12 rounded-full bg-white/20 backdrop-blur-md flex items-center justify-center border border-white/30 shadow-sm">
                                                                <span className="text-xl font-bold text-white drop-shadow-md">
                                                                    {endPositionArea ? area.id === endPositionArea ? "End" : (area.order ?? index + 1) : (area.order ?? index + 1)}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    );
                                                })()}

                                                {/* Status Indicators (Mute / Fill) */}
                                                <div className="absolute top-9 right-2 flex flex-col gap-1 pointer-events-none">
                                                    {Number(area.settings?.live?.is_muted) === 1 && (
                                                        <div className="w-6 h-6 rounded-full bg-black/60 backdrop-blur-sm flex items-center justify-center border border-white/20 shadow-sm" title="Muted">
                                                            <i className="fa-solid fa-volume-xmark text-[10px] text-white"></i>
                                                        </div>
                                                    )}
                                                    {Number(area.settings?.live?.maintain_aspect_ratio) === 0 && (
                                                        <div className="w-6 h-6 rounded-full bg-indigo-600/80 backdrop-blur-sm flex items-center justify-center border border-white/20 shadow-sm" title="Fill Screen">
                                                            <i className="bx bx-expand text-xs text-white"></i>
                                                        </div>
                                                    )}
                                                </div>

                                                {/* "on the back" Badge */}
                                                {area.order && area.order > 1 && (() => {
                                                    const isOverlapped = areas.some(otherArea => {
                                                        if (otherArea.id === area.id) return false;
                                                        if ((otherArea.order || 999) >= area.order) return false;
                                                        const otherRect = getAreaRect(otherArea);
                                                        const x_overlap = Math.max(0, Math.min(currentRect.x + currentRect.w, otherRect.x + otherRect.w) - Math.max(currentRect.x, otherRect.x));
                                                        const y_overlap = Math.max(0, Math.min(currentRect.y + currentRect.h, otherRect.y + otherRect.h) - Math.max(currentRect.y, otherRect.y));
                                                        return x_overlap > 1 && y_overlap > 1;
                                                    });
                                                    if (!isOverlapped) return null;
                                                    const position = toolsState?.areaNumbers?.position || 'center';
                                                    let positionClasses = '';
                                                    switch(position) {
                                                        case 'top': positionClasses = 'top-14 left-1/2 -translate-x-1/2 mt-4'; break;
                                                        case 'bottom': positionClasses = 'bottom-6 left-1/2 -translate-x-1/2 -mb-4'; break;
                                                        case 'center':
                                                        default: positionClasses = 'top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 mt-14'; break;
                                                    }
                                                    return (
                                                        <div className={`absolute ${positionClasses} pointer-events-none`}>
                                                            <div className="px-2 py-0.5 rounded bg-black/70 backdrop-blur-sm border border-white/30 shadow-lg">
                                                                <span className="text-[9px] uppercase font-bold text-white/95 tracking-wide">
                                                                    on the back
                                                                </span>
                                                            </div>
                                                        </div>
                                                    );
                                                })()}
                                            </div>
                                        );
                                    })}
                                </div>
                            </>
                        )
                    })()}



                         </div>
                    </div>
                </div>
            </div>
        }
    </>
    );
};

export default DynamicContainer;